import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, CloseOutlined, Search } from "@mui/icons-material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";

import api from "../../../../services/api";
import DatagridComponent from "../../../../components/datagridComponent";
import useClientesPaginados from "../../../../components/clientesPaginados/useClientesPaginados";
import useStyles from "./styles";

// Elegíveis: não é grupo e ainda não pertence a um grupo
const FILTRO_ELEGIVEIS = { grupoEconomico: "sem" };

export default function ModalCriarGrupo({
  openModal,
  setOpenModal,
  setSnackbar,
  onCreated,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [nome, setNome] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [nullCheck, setNullCheck] = useState(false);
  const [totalJaAgrupados, setTotalJaAgrupados] = useState(0);

  const { busca, setBusca, linhas, carregando, reiniciar, opcoesGrid } =
    useClientesPaginados(FILTRO_ELEGIVEIS, { ativo: openModal });

  const modeloSelecao = useMemo(() => [...selecionados], [selecionados]);

  useEffect(() => {
    if (!openModal) return;
    api
      .post("/interno/find/clientesPaginado", {
        grupoEconomico: "com",
        tamanho: 1,
      })
      .then((resposta) => setTotalJaAgrupados(resposta?.data?.total || 0))
      .catch((error) => console.error(error));
  }, [openModal]);

  const columns = [
    {
      field: "nome",
      headerName: "Cliente",
      flex: 1,
      renderCell: (params) =>
        `${params.row.clienteErpId} - ${params.row.nome || "Sem nome"}`,
    },
  ];

  const handleCancelar = () => {
    setOpenModal(false);
    setNome("");
    reiniciar();
    setSelecionados(new Set());
    setNullCheck(false);
  };

  const handleCriarGrupo = async () => {
    setNullCheck(true);
    if (!nome?.trim()) {
      setSnackbar({
        children: "Informe o nome do grupo",
        severity: "error",
      });
      return;
    }
    if (!selecionados.size) {
      setSnackbar({
        children: "Selecione ao menos um cliente para o grupo",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/interno/create/grupoEconomico", {
        nome: nome.trim(),
        clienteIds: [...selecionados],
      });
      setSnackbar({
        children: "Grupo econômico criado com sucesso",
        severity: "success",
      });
      onCreated?.(response?.data);
      handleCancelar();
    } catch (error) {
      console.error(error);
      setSnackbar({
        children:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao criar o grupo econômico",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={openModal}
      onClose={handleCancelar}
    >
      <Paper sx={styles.container}>
        <Box sx={styles.containerTitulo}>
          <Typography variant="title" sx={{}}>
            Criar Grupo Econômico
          </Typography>
          <IconButton onClick={handleCancelar}>
            <CloseOutlined />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={styles.containerForm}>
          <TextField
            label="Nome do grupo"
            id="nomeGrupo"
            variant="filled"
            color="primary"
            size="small"
            fullWidth
            autoComplete="off"
            value={nome}
            onChange={(e) => setNome(e?.target?.value)}
            helperText={
              !nome?.trim() && nullCheck
                ? "Necessário informar o nome do grupo"
                : ""
            }
            error={!nome?.trim() && nullCheck}
          />

          <Box sx={styles.boxClientes}>
            <Box sx={styles.containerClientes}>
              <Box>
                <Box sx={styles.titleClientes}>
                  <WorkspacesOutlinedIcon sx={{ fontSize: "22px" }} />
                  <Typography sx={{ fontWeight: "bold" }}>
                    Clientes do grupo
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "12px",
                    opacity: 0.5,
                  }}
                >
                  {`Selecione os clientes que farão parte do grupo econômico.${
                    totalJaAgrupados > 0
                      ? ` ${totalJaAgrupados} cliente(s) já pertencem a outro grupo e não são exibidos.`
                      : ""
                  }`}
                </Typography>
              </Box>

              {/* Campo de Busca */}
              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar cliente."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                sx={{ minWidth: "300px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {busca && (
                        <IconButton
                          size="small"
                          onClick={() => setBusca("")}
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Paper variant="outlined" sx={styles.paperDatagrid}>
              <DatagridComponent
                isPagination
                loading={carregando}
                options={{
                  ...opcoesGrid,
                  checkboxSelection: true,
                  // Mantém a seleção das outras páginas e buscas
                  keepNonExistentRowsSelected: true,
                  rowSelectionModel: modeloSelecao,
                  onRowSelectionModelChange: (modelo) =>
                    setSelecionados(new Set(modelo)),
                  disableRowSelectionOnClick: true,
                }}
                rows={linhas}
                columns={columns}
              />
            </Paper>
            <Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
              {`${selecionados.size} cliente(s) selecionado(s)`}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box sx={styles.containerButtons}>
          <Button variant="outlined" onClick={handleCancelar}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleCriarGrupo}
          >
            Criar Grupo
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
